/**
 * Quill / react-quill-new tende a emitir &nbsp; (U+00A0) no HTML gerado.
 * Na edição em modo “código HTML”, isso polui o texto; normaliza para espaço comum.
 *
 * Quill 2 grava listas com marcador como `<ol><li data-list="bullet">` + `<span class="ql-ui">`.
 * Indentação usa `class="ql-indent-N"` no `<li>` (não margin inline) — ver CSS em index.css.
 *
 * @param {string} html
 * @returns {string}
 */
export function normalizeNbspForHtmlEditor(html) {
    if (html == null || typeof html !== 'string') return '';
    return html
        .replace(/\u00A0/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&#160;/gi, ' ')
        .replace(/&#xA0;/gi, ' ');
}

/** @param {Element} el */
function getQlIndentLevel(el) {
    let max = 0;
    for (const c of Array.from(el.classList || [])) {
        const m = /^ql-indent-(\d+)$/.exec(c);
        if (m) max = Math.max(max, parseInt(m[1], 10) || 0);
    }
    return max;
}

/** @param {Element} el @param {number} level */
function setQlIndentLevel(el, level) {
    for (const c of Array.from(el.classList || [])) {
        if (c.startsWith('ql-indent-')) el.classList.remove(c);
    }
    const n = Math.min(8, Math.max(0, Math.floor(level)));
    if (n > 0) el.classList.add(`ql-indent-${n}`);
}

/**
 * Quill / conversões erradas podem gerar `<ul><li><ul><li>…` sem texto no li pai.
 * Achata para uma lista com `ql-indent-N`, como o editor visual do Quill.
 * @param {ParentNode} root
 */
function flattenNestedListWrappers(root) {
    let changed = true;
    while (changed) {
        changed = false;
        for (const li of Array.from(root.querySelectorAll('li'))) {
            if (!li.isConnected || !li.parentElement) continue;

            const nestedLists = Array.from(li.children).filter(
                (c) => c.tagName === 'UL' || c.tagName === 'OL',
            );
            if (nestedLists.length !== 1) continue;

            const hasTextOutside = Array.from(li.childNodes).some((n) => {
                if (n.nodeType === Node.TEXT_NODE) return (n.textContent || '').trim().length > 0;
                if (n.nodeType === Node.ELEMENT_NODE && n.tagName !== 'UL' && n.tagName !== 'OL') {
                    return (n.textContent || '').trim().length > 0;
                }
                return false;
            });
            if (hasTextOutside) continue;

            const parentList = li.parentElement;
            if (!parentList || (parentList.tagName !== 'UL' && parentList.tagName !== 'OL')) continue;

            const wrapperIndent = getQlIndentLevel(li);
            const nested = nestedLists[0];
            const nestedItems = Array.from(nested.children).filter((c) => c.tagName === 'LI');
            if (nestedItems.length === 0) {
                li.remove();
                changed = true;
                continue;
            }

            let ref = li.nextSibling;
            nestedItems.forEach((nestedLi) => {
                const combined = wrapperIndent + 1 + getQlIndentLevel(nestedLi);
                setQlIndentLevel(nestedLi, combined);
                parentList.insertBefore(nestedLi, ref);
                ref = nestedLi.nextSibling;
            });
            li.remove();
            changed = true;
        }
    }
}

/**
 * Remove artefatos do Quill 2 e converte listas para HTML semântico (ul/ol + li).
 * Deve rodar antes de DOMPurify para preservar data-list enquanto decide ul vs ol.
 *
 * @param {string} html
 * @returns {string}
 */
export function normalizeQuillListHtml(html) {
    if (html == null || typeof html !== 'string') return '';
    const trimmed = html.trim();
    if (!trimmed || typeof DOMParser === 'undefined') return html;

    const doc = new DOMParser().parseFromString(
        `<body><div id="__geppetto_quill_norm__">${trimmed}</div></body>`,
        'text/html',
    );
    const root = doc.getElementById('__geppetto_quill_norm__');
    if (!root) return html;

    const cleanListItem = (li) => {
        li.querySelectorAll('.ql-ui').forEach((el) => el.remove());
        li.removeAttribute('data-list');
    };

    const listTypeOf = (li) => {
        const t = (li.getAttribute('data-list') || '').trim();
        if (t === 'bullet' || t === 'checked' || t === 'unchecked') return 'bullet';
        if (t === 'ordered') return 'ordered';
        if (li.querySelector('.ql-ui')) return 'bullet';
        return 'ordered';
    };

    flattenNestedListWrappers(root);

    const ols = Array.from(root.querySelectorAll('ol'));
    ols.reverse().forEach((ol) => {
        const items = Array.from(ol.children).filter((n) => n.tagName === 'LI');
        if (items.length === 0) {
            ol.remove();
            return;
        }
        const types = items.map(listTypeOf);
        items.forEach(cleanListItem);
        const allBullet = types.every((t) => t === 'bullet');
        if (allBullet) {
            const ul = doc.createElement('ul');
            while (ol.firstChild) ul.appendChild(ol.firstChild);
            ol.replaceWith(ul);
        }
    });

    flattenNestedListWrappers(root);

    root.querySelectorAll('li').forEach(cleanListItem);

    return root.innerHTML;
}
